import React from 'react';
import { Category } from '../../../models/Category';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { SearchOutlined } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

type Props = {
  category: Category;
};

function NavBarCategory({ category }: Props) {
  const navigate = useNavigate();
  const [selected, setSelected] = React.useState(false);

  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const submenuRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className="relative group">
      <motion.button
        ref={buttonRef}
        onMouseEnter={() => setSelected(true)}
        onMouseLeave={() => setSelected(false)}
        onClick={() => navigate('/szukaj?categoryId=' + category.id)}
        className={`flex items-center space-x-2 transition-all duration-200 px-4 py-2 rounded-lg hover:bg-slate-50 hover:shadow-sm shrink-0 ${
          selected
            ? 'bg-slate-50 font-medium text-slate-800 shadow-sm'
            : 'text-slate-700'
        }`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
      >
        {category.image && (
          <img alt={category.name} className="w-5 h-5 object-contain" />
        )}
        <span className="whitespace-nowrap">{category.name}</span>
      </motion.button>

      <AnimatePresence>
        {selected &&
          category.subCategories &&
          category.subCategories.length > 0 && (
            <>
              {/* Invisible hover bridge to prevent menu from disappearing when moving cursor to submenu */}
              <motion.div
                className="absolute top-[90%] w-full h-2 bg-transparent z-20"
                onMouseOver={() => setSelected(true)}
                onMouseLeave={() => setSelected(false)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              ></motion.div>

              <motion.div
                ref={submenuRef}
                id="subcategories"
                className="absolute left-0 origin-top-left mt-1 
                bg-white border border-gray-100 rounded-lg shadow-lg z-10 
                overflow-hidden"
                onMouseOver={() => setSelected(true)}
                onMouseLeave={() => setSelected(false)}
                style={{
                  width: 'auto',
                  minWidth: buttonRef.current
                    ? buttonRef.current.offsetWidth
                    : 'auto',
                }}
                initial={{
                  opacity: 0,
                  y: -10,
                  scaleY: 0.8,
                  transformOrigin: 'top',
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scaleY: 1,
                  transition: {
                    duration: 0.2,
                    ease: 'easeOut',
                  },
                }}
                exit={{
                  opacity: 0,
                  y: -5,
                  scaleY: 0.9,
                  transition: {
                    duration: 0.15,
                    ease: 'easeIn',
                  },
                }}
              >
                <div className="py-1">
                  {category.subCategories.map((subcategory, index) => (
                    <motion.div
                      key={subcategory.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{
                        opacity: 1,
                        x: 0,
                        transition: {
                          delay: index * 0.03,
                          duration: 0.2,
                        },
                      }}
                    >
                      <Link
                        className="group flex w-full text-start items-center px-4 py-2 hover:bg-slate-50 text-slate-700 hover:text-slate-900 transition-colors duration-150"
                        to={`/szukaj?categoryId=${subcategory.id}`}
                      >
                        <motion.div
                          className="flex items-center space-x-2"
                          whileHover={{ x: 3 }}
                          transition={{
                            type: 'spring',
                            stiffness: 400,
                            damping: 17,
                          }}
                        >
                          <SearchOutlined
                            className="text-slate-400 group-hover:text-slate-600"
                            fontSize="small"
                          />
                          {subcategory.image && (
                            <img
                              src={subcategory.image}
                              alt={subcategory.name}
                              className="w-5 h-5 object-contain"
                            />
                          )}
                          <span className="whitespace-nowrap">
                            {subcategory.name}
                          </span>
                        </motion.div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </>
          )}
      </AnimatePresence>
    </div>
  );
}

export default NavBarCategory;
